// packages/core/src/learning/core-modules/your-money-snapshot.ts
// ─────────────────────────────────────────────────────────────────────────
// NISK CORE PATH — Module 02: "Your Money Snapshot" (tier: control)
// 5 lessons + checkpoint quiz + final test. Assembled to the real
// Lesson / ModuleAssessment / LearningModule types.
// ─────────────────────────────────────────────────────────────────────────

import type {
	Lesson,
	LearningModule,
	ModuleAssessment,
} from '../LearningTypes';

const LESSON_1: Lesson = {
	id: 'your-money-snapshot--know-your-starting-line',
	title: 'Know your starting line',
	description:
		'You learn why your money snapshot is not a judgment of you; it is the starting line for better decisions.',
	pillar: 'money-snapshot',
	schoolsOfThought: [
		'personal-finance-basics',
		'adulting-life-skills',
		'life-design',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 1,
	tags: ['snapshot', 'money-basics', 'clarity', 'starting-point'],
	prerequisites: [],
	relatedLessons: ['your-money-snapshot--count-what-you-own'],
	concepts: [
		'money-snapshot',
		'starting-line',
		'financial-clarity',
		'no-shame',
	],
	blocks: [
		{
			type: 'paragraph',
			text: 'Your money snapshot is the clearest picture of where you stand right now. Not where you wish you were. Not where your parents think you should be. Not where social media makes you feel behind. Right now. Cash, debt, income, bills, savings, and anything else that affects your options.',
		},
		{
			type: 'paragraph',
			text: 'This matters because vague money feels scarier than clear money. When you avoid the numbers, your brain fills in the blanks with panic. You may think you are doing worse than you are, or you may miss a problem that is quietly growing. A snapshot turns the fog into something you can actually work with.',
		},
		{
			type: 'bullet_list',
			items: [
				'What cash do you have?',
				'What do you owe?',
				'What comes in each month?',
				'What has to go out?',
				'What options do you currently have?',
			],
		},
		{
			type: 'callout',
			icon: '📍',
			text: 'A snapshot is not a grade. It is a location pin. You cannot build a route until you know where you are.',
		},
		{
			type: 'paragraph',
			text: 'The biggest mistake is treating your snapshot like a moral score. If the number is messy, people start making it personal. “I’m bad with money.” “I’m behind.” “I ruined everything.” That reaction may feel real, but it does not help. Nisk is not asking you to judge yourself. It is asking you to stop driving blind.',
		},
		{
			type: 'example',
			label: 'Real example',
			text: 'Imagine someone has $600 in checking, $1,200 in savings, $3,500 in credit card debt, $1,900 monthly take-home pay, and $1,750 in monthly expenses. That snapshot is not “good” or “bad” by itself. It tells a story: they have some cash, debt pressure, and only about $150 of monthly breathing room. Now there is something to work with.',
		},
		{
			type: 'paragraph',
			text: 'A good snapshot should be simple enough to update. If it becomes a giant spreadsheet ritual, most people quit. Start with the numbers that actually change your decisions: cash, debt, income, core expenses, and monthly gap. You can add detail later. The first win is honesty, not perfection.',
		},
		{
			type: 'paragraph',
			text: 'Think of your snapshot like standing on a map. If you are lost, the first thing your phone needs is your current location. Money works the same way. Before you can decide whether to pay debt faster, build runway, move out, invest, cut spending, or earn more, you need the “you are here” dot.',
		},
		{
			type: 'bullet_list',
			items: [
				'No shame, just location',
				'No guessing, just numbers',
				'No perfection, just a usable picture',
				'No panic, just next steps',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Your snapshot does not define you; it shows you where to start.',
		},
	],
	audioScript:
		'Before you make any serious money move, you need a snapshot. Not a perfect financial plan. Not a ten-tab spreadsheet. Just a clear picture of where you are right now. How much cash do you have? What do you owe? What comes in each month? What has to go out? How much room is left? That is your starting line. And this matters because vague money creates fear. When you do not know the numbers, your brain starts guessing. Sometimes it makes things feel worse than they are. Sometimes it lets you ignore a problem that is quietly getting bigger. Either way, you are driving blind. The point of a snapshot is not to judge you. That is the part people get wrong. They look at their debt, low savings, messy spending, or small income and immediately turn it into an identity. “I’m bad with money.” “I’m behind.” “I messed everything up.” But a snapshot is not a moral score. It is a location pin. It says, “You are here.” That is it. And once you know where you are, you can build a route. Let’s say someone has six hundred in checking, twelve hundred in savings, thirty-five hundred in credit card debt, nineteen hundred a month coming in, and seventeen-fifty going out. That snapshot tells us something useful. There is some cash, some debt pressure, and about one hundred fifty dollars of breathing room each month. That person does not need shame. They need clarity. A good money snapshot should be simple enough that you will actually update it. Cash, debt, income, core expenses, monthly gap. Start there. You can add more later. The win is not looking impressive. The win is no longer guessing.',
	flashcards: [
		{
			id: 'your-money-snapshot--know-your-starting-line--fc-main',
			role: 'main_idea',
			front: 'What is a money snapshot?',
			back: 'A money snapshot is a clear picture of your cash, debt, income, expenses, and current financial room.',
		},
		{
			id: 'your-money-snapshot--know-your-starting-line--fc-watch',
			role: 'watch_out',
			front: 'What is the emotional trap with a money snapshot?',
			back: 'Treating it like a moral grade instead of a starting point.',
		},
		{
			id: 'your-money-snapshot--know-your-starting-line--fc-use',
			role: 'use_it',
			front: 'What numbers should a beginner snapshot include first?',
			back: 'Start with cash, debt, income, core expenses, and monthly gap.',
		},
	],
	applyAction: {
		type: 'openDashboard',
		label: 'Open your money snapshot',
	},
	revision: 1,
};

const LESSON_2: Lesson = {
	id: 'your-money-snapshot--count-what-you-own',
	title: 'Count what you own',
	description:
		'You learn how to separate useful assets from noise so your money picture starts with what is actually available.',
	pillar: 'money-snapshot',
	schoolsOfThought: [
		'personal-finance-basics',
		'adulting-life-skills',
		'cashflow-assets',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 1,
	tags: ['assets', 'cash', 'savings', 'snapshot'],
	prerequisites: ['your-money-snapshot--know-your-starting-line'],
	relatedLessons: ['your-money-snapshot--count-what-you-owe'],
	concepts: ['assets', 'available-cash', 'liquidity', 'money-picture'],
	blocks: [
		{
			type: 'paragraph',
			text: 'The first side of your snapshot is what you own. That includes money in checking, savings, cash apps, brokerage accounts, retirement accounts, and anything else that has real value. But not all “owned” money helps you the same way. Some money is available today. Some is locked up. Some technically counts, but should not be treated like rent money.',
		},
		{
			type: 'paragraph',
			text: 'This distinction matters because your brain may see one big number and feel safer than you really are. Having $2,000 in a retirement account is not the same as having $2,000 in checking for next month’s bills. Both can matter. They just do different jobs.',
		},
		{
			type: 'bullet_list',
			items: [
				'Checking helps with near-term bills',
				'Savings helps with runway',
				'Retirement money helps future you',
				'Investments may move up and down',
				'Personal items usually matter less than cash flow',
			],
		},
		{
			type: 'callout',
			icon: '🔎',
			text: 'The question is not only “What do I own?” It is “Which money can actually help me right now?”',
		},
		{
			type: 'paragraph',
			text: 'A clean snapshot separates liquid money from less-liquid money. Liquid just means easy to access. Cash in checking is liquid. Savings is usually liquid. A retirement account may be valuable, but it is not the same kind of tool. A car may have resale value, but you probably still need it to live. A laptop may be worth money, but it is not the same as an emergency fund.',
		},
		{
			type: 'example',
			label: 'Quick snapshot',
			text: 'Taylor has $400 in checking, $900 in savings, $1,500 in a retirement account, and a car worth maybe $7,000. The total value looks like $9,800. But Taylor’s near-term usable cushion is really closer to $1,300: checking plus savings. The car and retirement account matter, but they should not create fake comfort for next month’s bills.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is counting everything as if it all has the same job. That can lead to bad decisions. Someone might think, “I have almost $10,000 of stuff,” but still be one rent payment away from stress because most of that value cannot easily be used without causing another problem.',
		},
		{
			type: 'paragraph',
			text: 'Your snapshot should show layers. First: money available now. Second: money available soon. Third: long-term assets. This keeps you honest. It also helps you see progress faster because you are not only asking, “How much am I worth?” You are asking, “How protected am I?”',
		},
		{
			type: 'bullet_list',
			items: [
				'Available now',
				'Available soon',
				'Long-term value',
				'Hard-to-use value',
			],
		},
		{
			type: 'key_takeaway',
			text: 'What you own only helps if you understand what job each asset can actually do.',
		},
	],
	audioScript:
		'The first side of your money snapshot is what you own. Sounds simple, but there is a catch. Not everything you own helps you in the same way. Four hundred dollars in checking is different from four hundred dollars in a retirement account. Nine hundred dollars in savings is different from a car that might be worth seven thousand dollars. These things can all have value, but they do not all solve the same problem. That is why your snapshot needs to separate useful cash from long-term value. Checking helps you handle near-term bills. Savings helps with runway. Retirement money helps future you. Investments can matter, but they may move around. A car may have resale value, but if you need it for work, it is not really the same as cash sitting in an emergency fund. The mistake is adding everything together and feeling safer than you actually are. Let’s say Taylor has four hundred in checking, nine hundred in savings, fifteen hundred in a retirement account, and a car worth maybe seven thousand. On paper, that looks like almost ten thousand dollars of value. But Taylor’s near-term cushion is really thirteen hundred dollars: checking plus savings. That is the money that can help with bills, repairs, and surprises without creating another mess. The car and retirement account still matter. They just have different jobs. This is not about being technical. It is about being honest. Your money picture should answer two questions. What do I own? And which of those things can actually help me right now? Once you can see that, your snapshot gets cleaner. You stop confusing total value with usable breathing room.',
	flashcards: [
		{
			id: 'your-money-snapshot--count-what-you-own--fc-main',
			role: 'main_idea',
			front: 'Why should your snapshot separate liquid money from long-term assets?',
			back: 'Because available cash and long-term assets do different jobs.',
		},
		{
			id: 'your-money-snapshot--count-what-you-own--fc-watch',
			role: 'watch_out',
			front: 'What is the trap in counting everything you own as the same?',
			back: 'You may feel safer than you really are if most of the value is hard to use.',
		},
		{
			id: 'your-money-snapshot--count-what-you-own--fc-use',
			role: 'use_it',
			front: 'What are useful asset layers for a snapshot?',
			back: 'Available now, available soon, long-term value, and hard-to-use value.',
		},
	],
	applyAction: {
		type: 'openDashboard',
		label: 'Add what you own',
	},
	revision: 1,
};

const LESSON_3: Lesson = {
	id: 'your-money-snapshot--count-what-you-owe',
	title: 'Count what you owe',
	description:
		'You learn how to face debt clearly without shame so you can see what is pulling against your options.',
	pillar: 'money-snapshot',
	schoolsOfThought: [
		'personal-finance-basics',
		'debt-free-ramsey',
		'credit-optimization',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 1,
	tags: ['debt', 'liabilities', 'credit-cards', 'snapshot'],
	prerequisites: ['your-money-snapshot--count-what-you-own'],
	relatedLessons: ['your-money-snapshot--find-your-monthly-gap'],
	concepts: [
		'liabilities',
		'debt-clarity',
		'minimum-payments',
		'interest-pressure',
	],
	blocks: [
		{
			type: 'paragraph',
			text: 'The second side of your snapshot is what you owe. Credit cards, student loans, car loans, personal loans, buy-now-pay-later balances, family debt, unpaid bills, and anything else that has a claim on your future money. This part can feel uncomfortable, but avoiding it gives debt more power, not less.',
		},
		{
			type: 'paragraph',
			text: 'Debt is not a personality flaw. It is a financial force. Some debt may have helped you get through life. Some may have come from emergencies, low income, family pressure, school, a car, medical costs, or mistakes. The snapshot is not here to shame the story. It is here to show the weight.',
		},
		{
			type: 'bullet_list',
			items: [
				'Total balance',
				'Minimum payment',
				'Interest rate if known',
				'Due date',
				'What the debt was for',
			],
		},
		{
			type: 'callout',
			icon: '🧱',
			text: 'Debt pulls on future you. A snapshot shows how hard it is pulling.',
		},
		{
			type: 'paragraph',
			text: 'The most important thing is not just the total balance. It is also the monthly pressure. A $3,000 debt with a $90 minimum payment affects your monthly life differently than a $3,000 debt due immediately. A car loan with a $450 monthly payment affects your breathing room even if the interest rate is not extreme. The balance matters, but the payment pressure matters too.',
		},
		{
			type: 'example',
			label: 'Real example',
			text: 'Nina owes $2,800 on a credit card with a $95 minimum payment, $9,000 on a car loan with a $390 payment, and $600 to a family member with no formal due date. Her total debt is $12,400, but her monthly required debt pressure is at least $485 before she pays anything extra. That number matters because it reduces her monthly options.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is only looking at debt when something goes wrong. That turns debt into a jump scare. You open the app, see the number, feel awful, close the app, and repeat next month. A better move is to make debt visible in a boring way. Boring visibility beats emotional avoidance.',
		},
		{
			type: 'paragraph',
			text: 'You do not need a full payoff strategy in this lesson. That comes later. For now, the job is to stop hiding the weight. Write the debt down. Name the balance. Name the payment. Name the interest rate if you know it. Name the due date. The more clearly you can see it, the less it controls the whole room.',
		},
		{
			type: 'bullet_list',
			items: [
				'No hiding',
				'No moral score',
				'No guessing',
				'No fake calm',
				'Just the weight on the table',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Debt clarity is not debt freedom, but it is the first move toward getting your options back.',
		},
	],
	audioScript:
		'Counting what you owe can feel heavier than counting what you own. That is normal. Debt carries emotion. Credit cards, car loans, student loans, family debt, personal loans, unpaid bills, buy-now-pay-later balances — all of it can come with stress, regret, pressure, or embarrassment. But here is the reframe: debt is not a personality flaw. It is a financial force. It pulls on your future money. Your snapshot is not asking, “Are you a good person?” It is asking, “How hard is this pulling on your options?” That is a much more useful question. When you count debt, do not only write the total balance. Write the minimum payment too. The monthly pressure matters. A three thousand dollar balance with a ninety dollar minimum payment affects your life differently than three thousand dollars due right now. A car loan with a four hundred dollar payment may be manageable or crushing depending on your income and other bills. The total matters, but the monthly pull matters too. Let’s say Nina has twenty-eight hundred on a credit card, nine thousand on a car loan, and six hundred owed to a family member. Total debt: twelve thousand four hundred. But her monthly required debt pressure is at least four hundred eighty-five dollars before paying anything extra. That number changes her room. It affects saving, moving, investing, breathing, and choices. The trap is only looking at debt when something goes wrong. Then it feels like a jump scare. Open the app, feel bad, close it, repeat. A snapshot makes debt boring and visible. That is progress. You do not need the perfect payoff plan yet. You just need to stop hiding the weight.',
	flashcards: [
		{
			id: 'your-money-snapshot--count-what-you-owe--fc-main',
			role: 'main_idea',
			front: 'What should you track when counting what you owe?',
			back: 'Track total balance, minimum payment, interest rate if known, due date, and what the debt was for.',
		},
		{
			id: 'your-money-snapshot--count-what-you-owe--fc-watch',
			role: 'watch_out',
			front: 'Why is it not enough to know only the total debt balance?',
			back: 'Because monthly payment pressure affects your cash flow and options right now.',
		},
		{
			id: 'your-money-snapshot--count-what-you-owe--fc-use',
			role: 'use_it',
			front: 'What is the first move toward dealing with debt?',
			back: 'Make the debt visible without shame: balance, payment, rate, and due date.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'List your debt pressure',
		prompt: 'Write every debt balance you owe, then add the minimum payment and due date next to each one.',
	},
	revision: 1,
};

const LESSON_4: Lesson = {
	id: 'your-money-snapshot--find-your-monthly-gap',
	title: 'Find your monthly gap',
	description:
		'You learn how to calculate the gap between money coming in and money going out so your plan is based on reality.',
	pillar: 'money-snapshot',
	schoolsOfThought: [
		'personal-finance-basics',
		'frugality',
		'rich-life-ramit',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 1,
	tags: ['cash-flow', 'income', 'expenses', 'monthly-gap'],
	prerequisites: ['your-money-snapshot--count-what-you-owe'],
	relatedLessons: ['your-money-snapshot--make-the-snapshot-repeatable'],
	concepts: ['monthly-gap', 'cash-flow', 'surplus', 'deficit'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Your monthly gap is the difference between what comes in and what goes out. If you bring home $2,500 and spend $2,300, your gap is positive $200. If you bring home $2,500 and spend $2,700, your gap is negative $200. That one number tells you whether your current life is creating room or eating room.',
		},
		{
			type: 'paragraph',
			text: 'This number matters because goals need a gap. Saving needs a gap. Debt payoff needs a gap. Investing needs a gap. Moving out needs a gap. Even rest needs a gap, because stress gets louder when every dollar is already spoken for.',
		},
		{
			type: 'bullet_list',
			items: [
				'Positive gap means room is being created',
				'Zero gap means every dollar is already assigned',
				'Negative gap means something is being funded by debt, savings, or delay',
			],
		},
		{
			type: 'callout',
			icon: '➖',
			text: 'The monthly gap is the truth number. It shows whether your current setup is giving you more options or fewer.',
		},
		{
			type: 'paragraph',
			text: 'The mistake is building goals from vibes. Someone says, “I want to save $500 a month,” but their real gap is only $80. That plan is not discipline. It is fiction. Another person says, “I can’t save anything,” but their snapshot shows $250 leaking into things they do not care about. That is not hopeless. It is hidden room.',
		},
		{
			type: 'example',
			label: 'Quick math',
			text: 'Marcus brings home $2,800 a month. His rent, car, insurance, phone, food, debt payments, gas, subscriptions, and normal spending total about $2,650. His monthly gap is $150. That means any plan requiring $400 a month needs either more income, lower expenses, or a slower timeline. The math is not insulting him. It is telling the truth.',
		},
		{
			type: 'paragraph',
			text: 'A monthly gap can be uncomfortable because it removes fantasy. If the gap is negative, something has to change. That does not mean you failed. It means your current setup is too tight for the life you are trying to build. You may need smaller goals, fewer leaks, lower fixed costs, more income, or time to stabilize.',
		},
		{
			type: 'paragraph',
			text: 'The mental model is pressure in a pipe. Income flows in. Expenses flow out. If everything flows out immediately, there is no pressure left to push goals forward. A gap creates pressure. Even a small gap can move your life if it is protected and repeated.',
		},
		{
			type: 'bullet_list',
			items: [
				'Calculate income after taxes',
				'Estimate real monthly spending',
				'Subtract spending from income',
				'Use the gap to size your next move',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Your monthly gap tells you how much room your current life is actually creating.',
		},
	],
	audioScript:
		'Your monthly gap is one of the most important numbers in your money life. It is also one of the simplest. What comes in minus what goes out. If you bring home twenty-five hundred dollars and spend twenty-three hundred, your gap is positive two hundred. If you bring home twenty-five hundred and spend twenty-seven hundred, your gap is negative two hundred. That number tells the truth. Is your current setup creating room, staying flat, or quietly digging a hole? This matters because almost every goal needs a gap. Saving needs a gap. Debt payoff needs a gap. Investing needs a gap. Moving out needs a gap. Building runway needs a gap. If there is no room between income and expenses, the plan has nowhere to live. This is where people accidentally lie to themselves. Someone says, “I’m going to save five hundred a month,” but their real gap is eighty dollars. That is not a plan. That is a wish. Someone else says, “I can’t save anything,” but their spending shows two hundred fifty dollars leaking into things they barely care about. That is not hopeless. That is hidden room. The monthly gap removes fantasy. And yes, that can sting. If the gap is negative, something is funding the difference — credit cards, savings, late payments, stress, or somebody else. But the number is not insulting you. It is showing where pressure is building. Think of money like a pipe. Income flows in. Expenses flow out. If everything flows out immediately, nothing is left to push your life forward. A gap creates pressure. Even a small gap, protected over time, can start changing your options.',
	flashcards: [
		{
			id: 'your-money-snapshot--find-your-monthly-gap--fc-main',
			role: 'main_idea',
			front: 'What is your monthly gap?',
			back: 'Your monthly gap is money coming in minus money going out.',
		},
		{
			id: 'your-money-snapshot--find-your-monthly-gap--fc-watch',
			role: 'watch_out',
			front: 'What is the trap in setting goals without knowing your monthly gap?',
			back: 'You may build goals from vibes instead of reality.',
		},
		{
			id: 'your-money-snapshot--find-your-monthly-gap--fc-use',
			role: 'use_it',
			front: 'How do you use your monthly gap?',
			back: 'Use it to size your savings, debt payoff, runway, or next money move realistically.',
		},
	],
	applyAction: {
		type: 'analyzeSavingsRate',
		label: 'Analyze your monthly gap',
	},
	revision: 1,
};

const LESSON_5: Lesson = {
	id: 'your-money-snapshot--make-the-snapshot-repeatable',
	title: 'Make the snapshot repeatable',
	description:
		'You learn how to turn your money snapshot into a simple check-in habit instead of a one-time cleanup.',
	pillar: 'money-snapshot',
	schoolsOfThought: [
		'personal-finance-basics',
		'behavioral-finance',
		'adulting-life-skills',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 1,
	tags: ['check-in', 'habit', 'dashboard', 'money-routine'],
	prerequisites: ['your-money-snapshot--find-your-monthly-gap'],
	relatedLessons: ['your-money-snapshot--know-your-starting-line'],
	concepts: [
		'money-check-in',
		'repeatable-system',
		'behavior',
		'progress-tracking',
	],
	blocks: [
		{
			type: 'paragraph',
			text: 'A money snapshot only helps if you come back to it. One clean-up session feels good, but life keeps moving. Bills change. Income changes. Debt changes. Spending changes. Plans change. If your snapshot is never updated, it becomes a photo of an old version of your life.',
		},
		{
			type: 'paragraph',
			text: 'The goal is not to obsess over your money every day. That can create anxiety and make normal spending feel like failure. The better move is a repeatable check-in. Short, honest, and boring enough that you will actually do it.',
		},
		{
			type: 'bullet_list',
			items: [
				'Check cash',
				'Check debt',
				'Check income',
				'Check core expenses',
				'Check monthly gap',
				'Choose one next action',
			],
		},
		{
			type: 'callout',
			icon: '🔁',
			text: 'The best money system is not the most detailed one. It is the one you actually return to.',
		},
		{
			type: 'paragraph',
			text: 'A repeatable snapshot can be weekly or monthly. Weekly is useful if your money is tight, your spending is unpredictable, or you are trying to stop leaks. Monthly is useful once things are more stable. The exact schedule matters less than the habit of looking before the numbers surprise you.',
		},
		{
			type: 'example',
			label: 'Simple check-in',
			text: 'Every Sunday, Jade spends 10 minutes checking checking balance, savings, credit card balance, upcoming bills, and whether the month is still on track. She does not solve her entire financial life every Sunday. She just catches problems early enough that they stay smaller.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is turning the snapshot into a giant self-improvement ceremony. New spreadsheet, new app, new categories, new rules, new color coding, new guilt. That can feel productive, but it often collapses because it is too heavy. Your snapshot should be light enough to survive a busy week.',
		},
		{
			type: 'paragraph',
			text: 'A good check-in ends with one action, not twenty. Move $25 to savings. Pay one bill. Cancel one unused subscription. Update one debt balance. Adjust one category. Plan one cheap grocery run. The action should be small enough to do now and useful enough to matter.',
		},
		{
			type: 'bullet_list',
			items: [
				'Look honestly',
				'Update the numbers',
				'Pick one action',
				'Close the loop',
			],
		},
		{
			type: 'paragraph',
			text: 'The mental model is maintenance, not rescue. You do not wait until your car breaks down to think about oil, tires, or gas. Money is similar. Small check-ins keep you from needing a dramatic rescue later.',
		},
		{
			type: 'key_takeaway',
			text: 'A snapshot becomes powerful when it turns into a simple routine you can repeat.',
		},
	],
	audioScript:
		'A money snapshot is not a one-time cleanup. It is a check-in. That matters because your money keeps moving even when you stop looking. Bills change. Income changes. Debt changes. Spending changes. Your plans change. If you only take a snapshot once and never return to it, you are looking at an old photo and pretending it is live. But the answer is not obsessing every day. That can make money feel like a punishment. The better answer is a simple routine. Check cash. Check debt. Check income. Check core expenses. Check your monthly gap. Choose one next action. That is enough. The best money system is not the fanciest one. It is the one you actually come back to. Weekly can work if money is tight or spending is unpredictable. Monthly can work if things are more stable. The schedule is less important than the habit of looking before the numbers surprise you. Imagine Jade spends ten minutes every Sunday checking her checking balance, savings, credit card balance, upcoming bills, and whether the month is still on track. She is not solving her whole life every Sunday. She is catching problems while they are still small. That is the point. The trap is making your system too heavy. New spreadsheet, new app, ten categories, twenty rules, color coding, guilt, shame, big promises. It feels productive for three days, then collapses. Keep it lighter. Look honestly. Update the numbers. Pick one useful action. Close the loop. Move twenty-five dollars. Pay one bill. Cancel one subscription. Update one balance. Money maintenance beats money rescue.',
	flashcards: [
		{
			id: 'your-money-snapshot--make-the-snapshot-repeatable--fc-main',
			role: 'main_idea',
			front: 'Why does a money snapshot need to be repeatable?',
			back: 'Because your money keeps changing, so an old snapshot stops being useful.',
		},
		{
			id: 'your-money-snapshot--make-the-snapshot-repeatable--fc-watch',
			role: 'watch_out',
			front: 'What is the trap in building a money check-in system?',
			back: 'Making it so detailed and heavy that you stop using it.',
		},
		{
			id: 'your-money-snapshot--make-the-snapshot-repeatable--fc-use',
			role: 'use_it',
			front: 'What should a simple money check-in end with?',
			back: 'One useful next action, such as moving money, paying a bill, canceling a leak, or updating a balance.',
		},
	],
	applyAction: {
		type: 'createBudgetPlan',
		label: 'Build a simple check-in plan',
	},
	revision: 1,
};

const ASSESSMENT: ModuleAssessment = {
	moduleId: 'your-money-snapshot',
	checkpointQuiz: [
		{
			type: 'multiple_choice',
			id: 'your-money-snapshot--cq--q1',
			question: 'What is the main purpose of a money snapshot?',
			options: [
				'To judge whether you are good or bad with money',
				'To show your current financial location so you can make better decisions',
				'To predict your exact future wealth',
				'To replace the need for budgeting',
			],
			correctIndex: 1,
			explanation:
				'A snapshot is a starting line, not a moral score or prediction.',
		},
		{
			type: 'true_false',
			id: 'your-money-snapshot--cq--q2',
			question:
				'A money snapshot should be treated like a grade of your worth as a person.',
			correct: false,
			explanation:
				'The module frames the snapshot as a location pin, not a judgment.',
		},
		{
			type: 'multiple_choice',
			id: 'your-money-snapshot--cq--q3',
			question:
				'Which set of numbers belongs in a beginner money snapshot?',
			options: [
				'Favorite stores, credit score guesses, and dream purchases',
				'Cash, debt, income, core expenses, and monthly gap',
				'Only retirement accounts',
				'Only spending categories',
			],
			correctIndex: 1,
			explanation:
				'The beginner snapshot focuses on the numbers that affect real decisions.',
		},
		{
			type: 'multiple_choice',
			id: 'your-money-snapshot--cq--q4',
			question:
				'Why should you separate liquid money from long-term assets?',
			options: [
				'Because long-term assets never matter',
				'Because all assets should be spent immediately',
				'Because available cash and long-term assets do different jobs',
				'Because checking accounts are the only real assets',
			],
			correctIndex: 2,
			explanation:
				'Money available now affects near-term stability differently than long-term or hard-to-use value.',
		},
		{
			type: 'scenario',
			id: 'your-money-snapshot--cq--q5',
			scenario:
				'Taylor has $300 in checking, $700 in savings, $2,000 in a retirement account, and a car they need for work.',
			question: 'What is Taylor’s clearest near-term usable cushion?',
			options: [
				'$300',
				'$1,000',
				'$3,000',
				'The full car value plus all accounts',
			],
			correctIndex: 1,
			explanation:
				'Checking plus savings gives the clearest near-term cushion. Other assets may matter, but they do not work the same way.',
		},
		{
			type: 'multiple_choice',
			id: 'your-money-snapshot--cq--q6',
			question: 'What debt details should a useful snapshot include?',
			options: [
				'Only the total balance',
				'Only the interest rate',
				'Balance, minimum payment, interest rate if known, and due date',
				'Only whether the debt feels stressful',
			],
			correctIndex: 2,
			explanation:
				'Debt affects both total wealth and monthly pressure, so the snapshot needs more than one number.',
		},
		{
			type: 'scenario',
			id: 'your-money-snapshot--cq--q7',
			scenario:
				'Nina owes $2,000 on a credit card and avoids opening the app because it makes her feel bad.',
			question:
				'What is the best next step from this module’s perspective?',
			options: [
				'Ignore it until she feels ready',
				'Make the debt visible without shame by writing the balance, payment, rate, and due date',
				'Assume the debt means she is bad with money',
				'Open a new account to forget about it',
			],
			correctIndex: 1,
			explanation:
				'Debt clarity is not debt freedom, but it is the first practical move.',
		},
	],
	finalTest: [
		{
			type: 'multiple_choice',
			id: 'your-money-snapshot--final--q1',
			question: 'Which sentence best describes Module 2?',
			options: [
				'Your snapshot is a clear picture of where your money stands right now',
				'Your snapshot is a final plan for the rest of your life',
				'Your snapshot is only useful for high earners',
				'Your snapshot should hide uncomfortable numbers',
			],
			correctIndex: 0,
			explanation:
				'The module teaches current clarity: cash, debt, income, expenses, and gap.',
		},
		{
			type: 'true_false',
			id: 'your-money-snapshot--final--q2',
			question: 'Vague money often feels scarier than clear money.',
			correct: true,
			explanation:
				'When numbers are unclear, the brain fills in the blanks with panic or avoidance.',
		},
		{
			type: 'multiple_choice',
			id: 'your-money-snapshot--final--q3',
			question:
				'What is the problem with adding up everything you own and treating it all the same?',
			options: [
				'It may create fake comfort because some value is hard to use',
				'It makes savings too easy',
				'It removes the need to track debt',
				'It automatically improves cash flow',
			],
			correctIndex: 0,
			explanation:
				'A car, retirement account, and checking account may all have value, but they do different jobs.',
		},
		{
			type: 'scenario',
			id: 'your-money-snapshot--final--q4',
			scenario:
				'Marcus brings home $2,800 per month and spends about $2,650.',
			question: 'What is his monthly gap?',
			options: ['$50', '$150', '$2,650', '$5,450'],
			correctIndex: 1,
			explanation:
				'$2,800 minus $2,650 leaves a positive monthly gap of $150.',
		},
		{
			type: 'multiple_choice',
			id: 'your-money-snapshot--final--q5',
			question: 'What does a negative monthly gap usually mean?',
			options: [
				'Everything is fine because income exists',
				'Something is being funded by debt, savings, late payments, or delay',
				'The person should never spend on fun',
				'The person has too much runway',
			],
			correctIndex: 1,
			explanation:
				'A negative gap means more is going out than coming in, so the difference must come from somewhere.',
		},
		{
			type: 'multiple_choice',
			id: 'your-money-snapshot--final--q6',
			question: 'Why does the monthly gap matter?',
			options: [
				'Almost every goal needs room between income and expenses',
				'It predicts investment returns',
				'It replaces the need to know debt balances',
				'It only matters after someone earns six figures',
			],
			correctIndex: 0,
			explanation:
				'Saving, debt payoff, runway, and future plans all need some kind of gap.',
		},
		{
			type: 'scenario',
			id: 'your-money-snapshot--final--q7',
			scenario:
				'Alex wants to save $500 per month, but their real monthly gap is only $80.',
			question: 'What is the most honest read?',
			options: [
				'The $500 goal is realistic because wanting it is enough',
				'The plan needs more income, lower expenses, or a slower timeline',
				'Alex should ignore the gap',
				'The snapshot is useless',
			],
			correctIndex: 1,
			explanation: 'Goals should be sized to reality, not vibes.',
		},
		{
			type: 'true_false',
			id: 'your-money-snapshot--final--q8',
			question:
				'A repeatable snapshot is better than a perfect system you never return to.',
			correct: true,
			explanation:
				'The module prioritizes a simple routine that survives real life.',
		},
		{
			type: 'multiple_choice',
			id: 'your-money-snapshot--final--q9',
			question: 'What should a simple money check-in usually end with?',
			options: [
				'A full life overhaul',
				'One useful next action',
				'A new complicated spreadsheet every week',
				'A reason to feel guilty',
			],
			correctIndex: 1,
			explanation:
				'A check-in should close with one practical move, not twenty.',
		},
		{
			type: 'scenario',
			id: 'your-money-snapshot--final--q10',
			scenario:
				'Jade checks her balances every Sunday for 10 minutes and chooses one next action.',
			question: 'What principle is Jade using?',
			options: [
				'Money maintenance beats money rescue',
				'More categories always means better control',
				'Money should be checked every hour',
				'A budget only works if it feels strict',
			],
			correctIndex: 0,
			explanation:
				'Small repeatable check-ins help catch problems before they become emergencies.',
		},
		{
			type: 'multiple_choice',
			id: 'your-money-snapshot--final--q11',
			question:
				'Which item is most clearly a liability in a money snapshot?',
			options: [
				'Checking balance',
				'Savings balance',
				'Credit card balance owed',
				'Monthly income',
			],
			correctIndex: 2,
			explanation:
				'A liability is something you owe that has a claim on future money.',
		},
		{
			type: 'scenario',
			id: 'your-money-snapshot--final--q12',
			scenario:
				'Sam has $1,200 in savings, $4,000 in credit card debt, $2,400 monthly take-home pay, and $2,300 monthly expenses.',
			question: 'What does this snapshot most clearly show?',
			options: [
				'Sam has no useful information yet',
				'Sam has some cash cushion, debt pressure, and a small positive monthly gap',
				'Sam is automatically financially free',
				'Sam should ignore the debt because savings exists',
			],
			correctIndex: 1,
			explanation:
				'The snapshot gives a usable picture: some savings, debt weight, and about $100 of monthly room.',
		},
	],
};

export const YOUR_MONEY_SNAPSHOT_MODULE: LearningModule = {
	id: 'your-money-snapshot',
	title: 'Your Money Snapshot',
	description:
		'Get a clear, shame-free picture of where your money stands right now: what you own, what you owe, your monthly gap, and a routine to keep it current.',
	emoji: '📸',
	accentColor: '#c8ff00',
	tier: 'control',
	pillar: 'money-snapshot',
	order: 2,
	lessons: [LESSON_1, LESSON_2, LESSON_3, LESSON_4, LESSON_5],
	assessment: ASSESSMENT,
};
